<template>
  <div class="border-0 px-4 py-1 scroll-container" ref="manage_workflow">
    <div class="d-flex justify-content-between align-items-center mb-1 me-4">
      <NavigationBackButton />
      <div class="d-flex align-items-center gap-3">
        <span class="project-badge text-xs p-0 d-inline-flex align-items-center">
          <i class='bx bxs-bookmark-alt fs-5 me-1 text-primary-blue'></i>
          Entity Type > <span class="fw-bold p-1"> {{entity_type_name}}</span>
        </span>
      </div>
    </div>

    <div class="card-header card-nav-tabs card-header-primary shadow-2 rounded-lg mt-2">
      <div class="nav-tabs-navigation">
        <div class="nav-tabs-wrapper">
          <ul class="nav nav-tabs" data-tabs="tabs">
            <li class="rounded-lg me-2 nav-item">
              <a class="text-decoration-none text-x fw-bold nav-link text-white" href="#profile" data-toggle="tab">
                {{form_name}}
              </a>
            </li>
          </ul>
          <hr class="text-white" />
        </div>
      </div>
    </div>

    <div class="card rounded-lg border-0 shadow mt-2">
      <div class="d-flex justify-content-start align-items-start p-3 gap-2 flex-column">
        <p class="mt-2 fw-bold text-xs" v-if="workflow_statuses.length > 0">
          Statuses assigned
        </p>
        <p class="mt-2 fw-bold text-muted text-xs" v-else>
          No status assigned
        </p>
        <button class="btn btn-primary-dark btn-sm rounded-small text-xxs shadow text-dark" data-bs-toggle="modal"
          data-bs-target="#assign_status_to_form" @click="get_assign_form_status_data">
          Assign Status
        </button>
      </div>

      <div class="card-body">
        <!-- Status Cards -->
        <div v-for="(status, index) in workflow_statuses" :key="index"
          class="mb-3 border table-border-color rounded shadow-sm">

          <!-- Status Header -->
          <div class="d-flex justify-content-between align-items-center p-2 position-relative" role="button"
            @click="toggle_status(index)">
            <div class="order-badge">{{index + 1}}</div>
            <div class="text-xxs ps-4">{{ status.status }}</div>
            <div><i :class="status.expanded ? 'bi bi-chevron-up' : 'bi bi-chevron-down'"></i></div>
          </div>

          <!-- Status Content -->
          <div v-show="status.expanded" class="p-3 bg-light">
            <div class="container-fluid">
              <div class="card border-0 shadow-sm mb-4">
                <div class="card-header card-header-primary card-nav-tabs border shadow rounded-lg py-2">
                  <div class="nav-tabs-navigation">
                    <div class="nav-tabs-wrapper">
                      <ul class="nav nav-tabs" data-tabs="tabs">
                        <li class="border rounded-lg me-2 nav-item"
                          :class="[ active_tab === 'reviewers' ? 'active' : 'inactive' ]"
                          @click="active_tab = 'reviewers'">
                          <a class="text-white text-decoration-none text-xs nav-link" href="#reviewers">
                            Status Reviewers
                          </a>
                        </li>
                        <li class="border rounded-lg me-2 nav-item"
                          :class="[ active_tab === 'rules' ? 'active' : 'inactive' ]" @click="active_tab = 'rules'">
                          <a class="text-white text-decoration-none text-xs nav-link" href="#rules">
                            Transition Rules
                          </a>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Status Reviewers Tab -->
              <div v-if="active_tab === 'reviewers'" class="fade show">
                <!-- Add Status Reviewers Button -->
                <div class="mb-4 text-end">
                  <button class="btn btn-primary-dark btn-sm rounded-small text-xxs shadow text-dark"
                    @click="open_add_status_reviewers_modal(status)">
                    Add Status Reviewers
                  </button>
                </div>

                <!-- Existing Reviewers Table -->
                <div v-if="has_assigned_reviewers(status)" class="card border-0 shadow-sm mt-4">
                  <div class="card-body">
                    <h6 class="fw-bold text-dark mb-3 text-xxs">Assigned Reviewers</h6>

                    <div class="table-responsive">
                      <table class="table table-hover">
                        <thead class="custom-header">
                          <tr>
                            <th class="text-uppercase text-xxs font-weight-bolder opacity-7 text-start ps-3">
                              Details
                            </th>
                            <th class="text-uppercase text-xxs font-weight-bolder opacity-7 text-start">
                              Assignment Type
                            </th>
                            <th class="text-uppercase text-xxs font-weight-bolder opacity-7 text-start">
                              Actions
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          <template v-for="(reviewer, reviewer_index) in get_assigned_reviewers(status)" :key="reviewer_index">
                            <tr v-if="reviewer && (reviewer.type === 'user' || reviewer.type === 'role')">
                              <td class="text-xxs text-start ps-3">
                                <template v-if="reviewer.type === 'user' && reviewer.assigned_users">
                                  <div v-if="reviewer.type === 'user'">
                                    <div v-for="user in reviewer.assigned_users" :key="user.id">
                                      {{ user.first_name }} {{ user.last_name }} — {{ user.email }} ({{ user.role_name }})
                                    </div>
                                  </div>
                                </template>
                                <template v-else-if="reviewer.type === 'role' && reviewer.assigned_role">
                                   <div v-if="reviewer.type === 'role'">
                                    {{ reviewer.assigned_role.name }}
                                  </div>
                                </template>
                              </td>
                              <td class="text-xxs text-start">
                                <span class="fw-medium text-xxs" v-if="reviewer">
                                  {{ reviewer.type === 'user' ? 'User Based' : 'Role Based' }}
                                </span>
                              </td>
                              <td class="text-xxs text-start">
                                <div class="d-flex align-items-center gap-2" v-if="reviewer">
                                  <button class="btn btn-primary-dark btn-sm rounded-small text-xxs shadow text-dark"
                                    data-bs-toggle="modal" data-bs-target="#section_access_modal"
                                    @click="open_section_access_modal(status, reviewer_index, reviewer.type)">
                                    Section Access
                                  </button>

                                  <button v-if="reviewer.type === 'user' && reviewer.assigned_users && reviewer.assigned_users[0]"
                                    @click="confirm_unassign_status_reviewer_user(reviewer.assigned_users[0].id, status.id, reviewer_index)"
                                    class="btn btn-primary-dark btn-sm rounded-small text-xxs shadow text-dark">
                                    Unassign
                                  </button>

                                  <button v-if="reviewer.type === 'role' && reviewer.assigned_role"
                                    @click="confirm_unassign_status_reviewer_role(reviewer.assigned_role.id, status.id, reviewer_index)"
                                    class="btn btn-primary-dark btn-sm rounded-small text-xxs shadow text-dark">
                                    Unassign
                                  </button>

                                </div>
                              </td>
                            </tr>
                          </template>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Transition Rules Tab -->
              <div v-if="active_tab === 'rules'" class="fade show">
                <!-- Add Transition Rules Button -->
                <div class="mb-4 text-end">
                  <button class="btn btn-primary-dark btn-sm rounded-small text-xxs shadow text-dark"
                    @click="open_add_transition_rules_modal(status)">
                    Add Transition Rules
                  </button>
                </div>

                <!-- Existing Transition Rules Table -->
                <div v-if="status.existing_transition_rules && status.existing_transition_rules.length > 0"
                  class="card border-0 shadow-sm mb-4">
                  <div class="card-body">
                    <h6 class="fw-bold text-dark mb-3 text-xxs">Existing Transition Rules</h6>

                    <div class="table-responsive">
                      <table class="table table-hover">
                        <thead class="custom-header">
                          <tr>
                            <th class="text-uppercase text-xxs font-weight-bolder opacity-7 text-start ps-3">
                              To Status
                            </th>
                            <th class="text-uppercase text-xxs font-weight-bolder opacity-7 text-start">
                              Transition Type
                            </th>
                            <th class="text-uppercase text-xxs font-weight-bolder opacity-7 text-start">
                              Min Approvals
                            </th>
                            <th class="text-uppercase text-xxs font-weight-bolder opacity-7 text-start">
                              Conditions
                            </th>
                            <th class="text-uppercase text-xxs font-weight-bolder opacity-7 text-start">
                              Active
                            </th>
                            <th class="text-uppercase text-xxs font-weight-bolder opacity-7 text-start">
                              Actions
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr v-for="(rule, rule_index) in status.existing_transition_rules" :key="rule.id">
                            <td class="text-xxs fw-medium text-start ps-3 py-3">
                              {{ this.format_question_type(rule.to_status.status_name) }}
                            </td>
                            <td class="text-start py-3">
                              <span class="text-xxs fw-medium">
                                {{ rule.transition_type.name }}
                              </span>
                            </td>
                            <td class="text-xxs text-start py-3 fw-medium">
                              {{ rule.min_approvals }}
                            </td>
                            <td class="text-start py-3">
                              <span v-if="rule.condition && rule.condition.rules && rule.condition.rules.length > 0"
                                class="text-xxs fw-medium">
                                {{ rule.condition.rules.length }} condition(s)
                              </span>
                              <span v-else class="text-xxs text-muted">
                                No conditions
                              </span>
                            </td>
                            <td class="text-start py-3">
                              <span class="text-xxs fw-medium">
                                {{ rule.is_active ? 'Active' : 'Inactive' }}
                              </span>
                            </td>
                            <td class="text-start py-3">
                              <div class="d-flex gap-2">
                                <button
                                  class="btn btn-primary-dark btn-sm rounded-small text-xxs shadow text-dark"
                                  @click="open_edit_rule_modal(index, rule_index)">
                                  Edit
                                </button>

                                <button class="btn btn-primary-dark btn-sm rounded-small text-xxs shadow text-dark"
                                  @click="confirm_delete_rule(rule.id, index, rule_index)">
                                  Delete
                                </button>
                              </div>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Save Button -->
          <div class="border-0 p-2 d-flex justify-content-end gap-2 mt-3 d-none">
            <button class="btn btn-primary-dark btn-sm rounded-small text-xxs shadow" data-bs-toggle="modal"
              data-bs-target="#update_action">Edit</button>
            <button type="button" class="btn btn-primary-dark btn-sm rounded-small text-xxs shadow text-dark"
              @click="submit_approval_workflow">
              Save
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Add Status Reviewers Modal -->
    <div v-if="show_status_reviewer_modal" class="modal fade show d-block bg-black-50" tabindex="-1" 
      aria-labelledby="addStatusReviewersModalLabel" aria-hidden="true">
      <div class="modal-dialog modal-lg">
        <div class="modal-content bg-white rounded-3 shadow-lg">
          <div class="modal-header cute-header border-bottom-0">
            <h5 class="modal-title text-white text-xs" id="addStatusReviewersModalLabel">
              Add Status Reviewers
            </h5>
            <button type="button" class="modal-btn-close btn-close" aria-label="Close"
              @click="close_status_reviewer_modal"></button>
          </div>
          
          <div class="modal-body p-4">
            <!-- Step Indicator -->
            <div class="step-indicator mb-4">
              <div class="d-flex justify-content-between align-items-center">
                <div v-for="step in 4" :key="step" class="step-item">
                  <div class="btn-primary-dark rounded-circle d-inline-flex align-items-center justify-content-center w-40px h-40px" :class="{
                    'complete': status_reviewer_step > step,
                    'current': status_reviewer_step === step
                  }">
                    <span v-if="status_reviewer_step > step">✓</span>
                    <span v-else>{{ step }}</span>
                  </div>
                  <div class="step-title" :class="{ 'current': status_reviewer_step === step }">
                    {{ step === 1 ? 'Type' : step === 2 ? 'Role' : step === 3 ? 'Users' : 'Confirm' }}
                  </div>
                </div>
              </div>
            </div>

            <!-- Step Content -->
            <div class="step-content">
              <!-- Step 1: Assignment Type -->
              <div v-if="status_reviewer_step === 1" class="fade show">
                <h6 class="fw-bold text-dark mb-3 text-xxs">Choose Assignment Type</h6>
                <div class="row">
                  <div class="col-md-6 mb-3">
                    <div class="option-card" :class="{ 'selected': status_reviewer_data.assignment_type === 'user' }"
                      @click="select_assignment_type('user')">
                      <div class="option-icon text-center mb-2">
                        <i class="bi bi-people fs-3"></i>
                      </div>
                      <h6 class="fw-bold text-dark text-xxs mb-1">Assign Specific Users</h6>
                      <p class="text-muted text-xxs mb-0">Select individual users as reviewers</p>
                    </div>
                  </div>
                  <div class="col-md-6 mb-3">
                    <div class="option-card" :class="{ 'selected': status_reviewer_data.assignment_type === 'role' }"
                      @click="select_assignment_type('role')">
                      <div class="option-icon text-center mb-2">
                        <i class="bi bi-person-badge fs-3"></i>
                      </div>
                      <h6 class="fw-bold text-dark text-xxs mb-1">Assign by Role</h6>
                      <p class="text-muted text-xxs mb-0">All users with selected role become reviewers</p>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Step 2: Select Role -->
              <div v-if="status_reviewer_step === 2" class="fade show">
                <h6 class="fw-bold text-dark mb-3 text-xxs">Select Role</h6>
                <select class="form-select form-select-sm text-xxs mb-3" v-model="status_reviewer_data.selected_role"
                  @change="get_users_by_role_for_modal">
                  <option value="">Choose a role...</option>
                  <option v-for="role in roles" :key="role.id" :value="role.id">
                    {{ role.role }}
                  </option>
                </select>
              </div>

              <!-- Step 3: Choose Users (only for user assignment) -->
              <div v-if="status_reviewer_step === 3 && status_reviewer_data.assignment_type === 'user'" class="fade show">
                <h6 class="fw-bold text-dark mb-3 text-xxs">Select Users to Assign</h6>
                
                <div class="input-group input-group-sm mb-3 rounded-2">
                  <span class="input-group-text bg-white border-end-0 rounded-start-2">
                    <i class="bi bi-search text-muted"></i>
                  </span>
                  <input type="text" class="form-control text-xxs border-start-0 rounded-end-2 shadow-none"
                    v-model="status_reviewer_data.user_search" placeholder="Search by name or email..." />
                </div>

                <div class="border rounded-3 overflow-auto bg-white max-h-250px">
                  <div v-for="user in filtered_modal_users" :key="user.id" 
                    class="d-flex align-items-center px-3 py-2 border-bottom bg-white">
                    <div class="form-check mb-0 w-100 d-flex align-items-center">
                      <input class="form-check-input me-2" type="checkbox" :value="user.id"
                        v-model="status_reviewer_data.selected_users"
                        :id="'modal_user_' + user.id" />
                      <label class="form-check-label text-xxs w-100 d-flex justify-content-between"
                        :for="'modal_user_' + user.id">
                        <span class="fw-medium">{{ user.first_name }} {{ user.last_name }}</span>
                        <span class="text-muted">{{ user.email }}</span>
                      </label>
                    </div>
                  </div>
                </div>
                
                <div v-if="status_reviewer_data.selected_users.length > 0" class="mt-2">
                  <div class="alert alert-light text-xxs py-2 px-3 mb-2 border rounded-2 bg-light">
                    {{ status_reviewer_data.selected_users.length }} user(s) selected
                  </div>
                </div>
              </div>

              <!-- Step 4: Confirm -->
              <div v-if="status_reviewer_step === 4" class="fade show">
                <h6 class="fw-bold text-dark mb-3 text-xxs">Review & Confirm</h6>
                <div class="summary-card border rounded p-3 mb-3">
                  <h6 class="fw-bold text-dark text-xxs mb-2">Assignment Summary</h6>
                  <div class="summary-row d-flex justify-content-between mb-2">
                    <span class="summary-label text-xxs text-muted">Assignment Type:</span>
                    <span class="summary-value text-xxs fw-bold">
                      {{ status_reviewer_data.assignment_type === 'user' ? 'Specific Users' : 'Role-Based' }}
                    </span>
                  </div>
                  <div class="summary-row d-flex justify-content-between mb-2">
                    <span class="summary-label text-xxs text-muted">Selected Role:</span>
                    <span class="summary-value text-xxs fw-bold">
                      {{ get_role_name(status_reviewer_data.selected_role) }}
                    </span>
                  </div>
                  <div v-if="status_reviewer_data.assignment_type === 'user'" class="summary-row d-flex justify-content-between">
                    <span class="summary-label text-xxs text-muted">Users Assigned:</span>
                    <span class="summary-value text-xxs fw-bold d-flex flex-column align-items-end text-end">
                    <span v-for="uid in status_reviewer_data.selected_users" :key="uid">
                      {{
                        (filtered_modal_users.find(u => u.id === uid)?.first_name || '')
                      }}
                      {{
                        (filtered_modal_users.find(u => u.id === uid)?.last_name || '')
                      }}
                      ({{ filtered_modal_users.find(u => u.id === uid)?.email || '' }})
                    </span>
                  </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="modal-footer border-0">
            <button type="button" class="btn btn-primary-dark btn-sm rounded-small text-xxs shadow text-dark" 
              v-if="status_reviewer_step > 1" @click="go_to_previous_status_step">
              Back
            </button>
            <button type="button" class="btn btn-primary-dark btn-sm rounded-small text-xxs shadow text-dark"
              @click="go_to_next_status_step">
              {{ status_reviewer_step === 4 ? 'Save Assignment' : 'Next' }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Add Transition Rules Modal -->
 <div v-if="show_transition_rule_modal" class="modal fade show d-block bg-black-50" tabindex="-1" 
   aria-labelledby="addTransitionRulesModalLabel" aria-hidden="true">
  <div class="modal-dialog modal-xl modal-dialog-scrollable">
    <div class="modal-content bg-white rounded-3">
      <!-- Header - Updated to match your styling -->
      <div class="modal-header border-bottom-0 custom-header text-white">
        <h5 class="modal-title text-xxs" id="addTransitionRulesModalLabel">
          Add Transition Rule
        </h5>
        <button type="button" class="btn-close" @click="close_transition_rule_modal" aria-label="Close"></button>
      </div>

      <!-- Body -->
      <div class="modal-body p-4">
        <!-- Step Indicator -->
        <div class="step-indicator mb-4">
          <div class="d-flex justify-content-between align-items-center">
            <div v-for="step in 4" :key="step" class="step-item">
              <div class="btn-primary-dark rounded-circle d-inline-flex align-items-center justify-content-center w-40px h-40px" :class="{
                'complete': transition_rule_step > step,
                'current': transition_rule_step === step
              }">
                <span v-if="transition_rule_step > step">✓</span>
                <span v-else>{{ step }}</span>
              </div>
              <div class="step-title" :class="{ 'current': transition_rule_step === step }">
                {{ step === 1 ? 'Flow' : step === 2 ? 'Type' : step === 3 ? 'Conditions' : 'Confirm' }}
              </div>
            </div>
          </div>
        </div>

        <!-- Step Content -->
        <div class="step-content">
          <!-- Step 1: Status Flow -->
          <div v-if="transition_rule_step === 1" class="fade show">
            <h6 class="fw-bold text-dark mb-3 text-xxs">Define Status Transition</h6>
            <div class="row align-items-center mb-4">
              <div class="col-md-5">
                <div class="form-group">
                  <label class="form-label text-xxs fw-bold mb-1">From Status</label>
                  <input type="text" class="form-control form-control-sm text-xxs bg-light" 
                         :value="current_status ? current_status.status : ''" disabled />
                </div>
              </div>
              <div class="col-md-2 text-center py-2">
                <div class="btn-primary-dark rounded-circle d-inline-flex align-items-center justify-content-center w-40px h-40px">
                  <i class="bi bi-arrow-right text-white"></i>
                </div>
              </div>
              <div class="col-md-5">
                <div class="form-group">
                  <label class="form-label text-xxs fw-bold mb-1">To Status</label>
                  <select class="form-select form-select-sm text-xxs" v-model="transition_rule_data.to_status_id">
                    <option value="">Select status...</option>
                    <option v-for="next_status in get_available_statuses(current_status ? current_status.id : '')" :key="next_status.id"
                            :value="next_status.id">
                      {{ next_status.status }}
                    </option>
                  </select>
                </div>
              </div>
            </div>
          </div>

          <!-- Step 2: Transition Type -->
          <div v-if="transition_rule_step === 2" class="fade show">
            <h6 class="fw-bold text-dark mb-3 text-xxs">Select Transition Type</h6>
            <div class="radio-group">
              <div class="radio-option mb-2" :class="{ 'selected': transition_rule_data.transition_type_id === 1 }"
                   @click="select_transition_type(1)">
                <input type="radio" name="transitionType" value="1" class="radio-input"
                       :checked="transition_rule_data.transition_type_id === 1"
                       @click="select_transition_type(1)" />
                <div class="radio-content">
                  <div class="radio-title text-xxs fw-bold">Manual Transition</div>
                  <div class="radio-description text-xxs text-muted">Requires explicit approval from reviewers</div>
                </div>
              </div>
              <div class="radio-option" :class="{ 'selected': transition_rule_data.transition_type_id === 2 }"
                   @click="select_transition_type(2)">
                <input type="radio" name="transitionType" value="2" class="radio-input"
                       :checked="transition_rule_data.transition_type_id === 2"
                       @click="select_transition_type(2)" />
                <div class="radio-content">
                  <div class="radio-title text-xxs fw-bold">Automatic Transition</div>
                  <div class="radio-description text-xxs text-muted">Transitions automatically when conditions are met</div>
                </div>
              </div>
            </div>

            <div v-if="transition_rule_data.transition_type_id === 1" class="mt-3">
              <h6 class="fw-bold text-dark mb-2 text-xxs">Minimum Approvals Required</h6>
              <input type="number" class="form-control form-control-sm text-xxs" 
                     v-model.number="transition_rule_data.min_approvals" min="1" />
            </div>
          </div>

          <!-- Step 3: Add Conditions -->
          <div v-if="transition_rule_step === 3" class="fade show">
            <h6 class="fw-bold text-dark mb-3 text-xxs">
              <i class="bi bi-funnel me-2"></i>Transition Conditions
            </h6>

            <div class="alert alert-info text-xxs mb-3">
              Conditions define additional requirements that must be met before this transition can occur.
            </div>

            <div v-if="transition_rule_data.conditions.length === 0" class="empty-state border rounded p-4 text-center">
              <div class="empty-icon mb-2">
                <i class="bi bi-funnel fs-3 text-muted"></i>
              </div>
              <div class="empty-text text-xxs text-muted mb-3">No conditions added yet</div>
              <button type="button" class="btn btn-primary-dark btn-sm rounded-small text-xxs shadow text-dark"
                      @click="add_condition_to_modal">
                Add Condition
              </button>
            </div>

            <!-- Conditions List -->
            <div v-for="(condition, condition_index) in transition_rule_data.conditions" :key="condition_index"
                 class="card border mb-3">
              <div class="card-header bg-white py-2 d-flex justify-content-between align-items-center">
                <span class="fw-semibold text-xxs">Condition {{ condition_index + 1 }}</span>
                <button type="button" class="btn btn-primary-dark btn-sm rounded-small text-xxs shadow text-dark py-1 px-2"
                        @click="remove_condition_from_modal(condition_index)">
                  Remove
                </button>
              </div>

              <div class="card-body">
                <!-- Row: Action Type | Required Role | Number of Approvals | Completion Type -->
                <div class="row g-3">
                  <div class="col-md-4">
                    <label class="text-xxs fw-bold mb-1">Action Type</label>
                    <select v-model="condition.action_type" class="form-select form-select-sm text-xxs">
                      <option value="">-- Select Action Type --</option>
                      <option value="section_completion">Section Completion</option>
                      <option value="question_completion">Question Completion</option>
                    </select>
                  </div>

                  <div class="col-md-4">
                    <label class="text-xxs fw-bold mb-1">Required Role</label>
                    <select v-model="condition.required_role" class="form-select form-select-sm text-xxs">
                      <option value="">-- Select Role --</option>
                      <option v-for="role in roles" :key="role.id" :value="role.id">{{ role.role }}</option>
                    </select>
                  </div>

                  <div class="col-md-4">
                    <label class="text-xxs fw-bold mb-1">Completion Type</label>
                    <select v-model="condition.completion_type" class="form-select form-select-sm text-xxs">
                      <option value="complete">Complete</option>
                      <option value="verified">Verified</option>
                    </select>
                  </div>
                </div>

                <!-- Section Completion -->
                <div v-if="condition.action_type === 'section_completion'" class="mt-3">
                  <h6 class="fw-bold text-dark mb-1 text-xxs">Select Sections</h6>
                  <div class="border rounded p-2 max-h-150px overflow-auto">
                    <input type="text" class="form-control form-control-sm mb-2 text-xxs" placeholder="Search sections..."
                           v-model="condition.sections_search" />
                    <div v-for="section in section_list.filter(s => !condition.sections_search || (s.name || s.status).toLowerCase().includes(condition.sections_search.toLowerCase()))"
                         :key="section.id" class="form-check mb-1">
                      <input type="checkbox" class="form-check-input"
                             :id="'section-'+condition_index+'-'+section.id"
                             v-model="condition.selected_sections"
                             :value="section.id" />
                      <label class="form-check-label text-xxs" :for="'section-'+condition_index+'-'+section.id">{{ section.status || section.name }}</label>
                    </div>
                  </div>
                </div>

                <!-- Question Completion -->
                <div v-if="condition.action_type === 'question_completion'" class="mt-3">
                  <h6 class="fw-bold text-dark mb-1 text-xxs">Select Sections for Questions</h6>
                  <div class="border rounded p-2 max-h-150px overflow-auto">
                    <input type="text" class="form-control form-control-sm mb-2 text-xxs" placeholder="Search sections..."
                           v-model="condition.sections_search" />
                    <div v-for="section in section_list.filter(s => !condition.sections_search || (s.name || s.status).toLowerCase().includes(condition.sections_search.toLowerCase()))"
                         :key="section.id" class="form-check mb-1">
                      <input type="checkbox" class="form-check-input"
                             :id="'q-section-'+condition_index+'-'+section.id"
                             v-model="condition.selected_sections_for_questions"
                             :value="section.id"
                             @change="load_questions_for_selected_sections(condition)" />
                      <label class="form-check-label text-xxs" :for="'q-section-'+condition_index+'-'+section.id">{{ section.status || section.name }}</label>
                    </div>
                  </div>

                  <!-- Questions under selected sections -->
                  <div v-for="section_id in condition.selected_sections_for_questions" :key="section_id" class="mt-2">
                    <h6 class="fw-bold text-dark mb-1 text-xxs">Questions in Section {{ get_section_name(section_id) }}</h6>
                    <div class="border rounded p-2 mb-2 max-h-150px overflow-auto">
                      <input type="text" class="form-control form-control-sm mb-2 text-xxs" placeholder="Search questions..."
                            v-model="condition.questions_search_map[section_id]" />
                      <div v-for="question in get_questions_for_section(section_id)
                          .filter(q => {
                            const search = (condition.questions_search_map?.[section_id] || '').toLowerCase().trim();
                            const text = (q.question_text || q.question || q.text || '').toLowerCase();
                            return !search || text.includes(search);
                          })"
                        :key="question.id"
                          class="form-check mb-1">
                        <input type="checkbox" class="form-check-input"
                              :id="'question-'+condition_index+'-'+question.id"
                              v-model="condition.selected_questions"
                              :value="question.id" />
                        <label class="form-check-label text-xxs" :for="'question-'+condition_index+'-'+question.id">
                          {{ get_question_display_text(question) }}
                        </label>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Actions Multi-Select -->
                <div class="mt-3">
                  <h6 class="fw-bold text-dark mb-1 text-xxs">Select Action(s)</h6>
                  <div class="border rounded p-2 max-h-150px overflow-auto">
                    <input type="text" class="form-control form-control-sm mb-2 text-xxs" placeholder="Search actions..."
                           v-model="condition.action_search" />
                    <div v-for="action in get_filtered_actions(condition)" :key="action.id" class="form-check mb-1">
                      <input type="checkbox" class="form-check-input"
                             :id="'action-'+condition_index+'-'+action.id"
                             v-model="condition.selected_actions"
                             :value="action.id" />
                      <label class="form-check-label text-xxs" :for="'action-'+condition_index+'-'+action.id">
                        {{ action.name }} <span class="text-muted">{{ action.description }}</span>
                      </label>
                    </div>
                  </div>
                </div>

              </div>
            </div>

            <div v-if="transition_rule_data.conditions.length > 0" class="mt-3">
              <button type="button" class="btn btn-primary-dark btn-sm rounded-small text-xxs shadow text-dark"
                      @click="add_condition_to_modal">
                Add Another Condition
              </button>
            </div>
          </div>

          <!-- Step 4: Review & Save -->
          <div v-if="transition_rule_step === 4" class="fade show">
            <h6 class="fw-bold text-dark mb-3 text-xxs">Review Transition Rule</h6>
            <div class="summary-card border rounded p-3 mb-3">
              <h6 class="fw-bold text-dark text-xxs mb-2">Transition Rule Summary</h6>
              <div class="summary-row d-flex justify-content-between mb-2">
                <span class="summary-label text-xxs text-muted">Status Flow:</span>
                <span class="summary-value text-xxs fw-bold">
                  {{ current_status ? current_status.status : '' }} → {{ get_status_name(transition_rule_data.to_status_id) }}
                </span>
              </div>
              <div class="summary-row d-flex justify-content-between mb-2">
                <span class="summary-label text-xxs text-muted">Transition Type:</span>
                <span class="summary-value text-xxs fw-bold">
                  {{ transition_rule_data.transition_type_id === 1 ? 'Manual' : 'Automatic' }}
                </span>
              </div>
              <div v-if="transition_rule_data.transition_type_id === 1" class="summary-row d-flex justify-content-between mb-2">
                <span class="summary-label text-xxs text-muted">Min Approvals:</span>
                <span class="summary-value text-xxs fw-bold">{{ transition_rule_data.min_approvals }}</span>
              </div>
              <div class="summary-row d-flex justify-content-between">
                <span class="summary-label text-xxs text-muted">Conditions:</span>
                <span class="summary-value text-xxs fw-bold">{{ transition_rule_data.conditions.length }} condition(s)</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Footer -->
      <div class="modal-footer border-0">
        <button type="button" class="btn btn-primary-dark btn-sm rounded-small text-xxs shadow text-dark" 
                v-if="transition_rule_step > 1" @click="go_to_previous_transition_step">
          Back
        </button>
        <button type="button" class="btn btn-primary-dark btn-sm rounded-small text-xxs shadow text-dark"
                @click="go_to_next_transition_step">
          {{ transition_rule_step === 4 ? 'Save Transition Rule' : 'Next' }}
        </button>
      </div>
    </div>
  </div>
</div>



    <!-- Assign Status Modal -->
    <teleport to="body">
      <div class="modal fade" id="assign_status_to_form" tabindex="-1" aria-labelledby="assign_status_to_form_label"
        aria-hidden="true">
        <div class="modal-dialog modal-lg">
          <div class="modal-content bg-white rounded-3">
            <div class="modal-header border-bottom-0 custom-header text-white">
              <h5 class="modal-title text-xxs" id="assign_status_to_form_label">Assign Status</h5>
              <button type="button" class="btn-close " data-bs-dismiss="modal" aria-label="Close"></button>
            </div>

            <div class="modal-body py-5">
              <table id="assign_form_status_table" class="table-responsive w-100 table table-hover">
                <thead class="custom-header">
                  <tr>
                    <th class="text-uppercase text-xxs font-weight-bolder opacity-7 text-start">ID</th>
                    <th class="text-uppercase text-xxs font-weight-bolder opacity-7 text-start">Status Name</th>
                    <th class="text-uppercase text-xxs font-weight-bolder opacity-7 text-start">Assign</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(status, index) in statuses" :key="status.ID">
                    <td class="text-xxs text-start">{{ status.ID }}</td>
                    <td class="text-xxs text-start">{{ status.status_name }}</td>
                    <td class="text-xxs text-start w-25">
                      <div class="form-check form-switch d-flex">
                        <input class="form-check-input" type="checkbox" v-model="status.is_assigned"
                          :checked="status.is_assigned" @change="handle_assignment_toggle(status)">
                      </div>
                      <div class="min-vh-1 text-wrap">
                        <small v-if="status.show_validation" class="text-danger text-xxs">Please select an order number
                          before
                          assigning status.</small>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </teleport>

    <!-- Edit Transition Rule Modal -->
  <teleport to="body">
    <div v-if="editing_rule" class="modal fade show d-block bg-black-50" tabindex="-1" 
     aria-labelledby="editTransitionRuleModalLabel" aria-hidden="true">
  <div class="modal-dialog modal-xl modal-dialog-scrollable">
    <div class="modal-content bg-white rounded-3">

      <!-- Header -->
      <div class="modal-header border-bottom-0 custom-header text-white">
        <h5 class="modal-title text-xxs" id="editTransitionRuleModalLabel">
          Edit Transition Rule
        </h5>
        <button type="button" class="btn-close" @click="reset_edit_modal" aria-label="Close"></button>
      </div>

      <!-- Body -->
      <div class="modal-body p-4">

        <!-- Step Indicator -->
        <div class="step-indicator mb-4">
          <div class="d-flex justify-content-between align-items-center">
            <div v-for="step in 4" :key="step" class="step-item">
              <div class="btn-primary-dark rounded-circle d-inline-flex align-items-center justify-content-center w-40px h-40px" 
                   :class="{ 'complete': editing_step > step, 'current': editing_step === step }">
                <span v-if="editing_step > step">✓</span>
                <span v-else>{{ step }}</span>
              </div>
              <div class="step-title" :class="{ 'current': editing_step === step }">
                {{ step === 1 ? 'Flow' : step === 2 ? 'Type' : step === 3 ? 'Conditions' : 'Confirm' }}
              </div>
            </div>
          </div>
        </div>

        <!-- Step Content -->
        <div class="step-content">

          <!-- Step 1: Status Flow -->
          <div v-if="editing_step === 1" class="fade show">
            <h6 class="fw-bold text-dark mb-3 text-xxs">Edit Status Transition</h6>
            <div class="row align-items-center mb-4">
              <div class="col-md-5">
                <div class="form-group">
                  <label class="form-label text-xxs fw-bold mb-1">From Status</label>
                  <input type="text" class="form-control form-control-sm text-xxs bg-light" 
                         :value="workflow_statuses[editing_status_index]?.status || ''" disabled />
                </div>
              </div>
              <div class="col-md-2 text-center py-2">
                <div class="btn-primary-dark rounded-circle d-inline-flex align-items-center justify-content-center w-40px h-40px">
                  <i class="bi bi-arrow-right text-white"></i>
                </div>
              </div>
              <div class="col-md-5">
                <div class="form-group">
                  <label class="form-label text-xxs fw-bold mb-1">To Status</label>
                  <select class="form-select form-select-sm text-xxs" v-model="editing_rule.to_status_id">
                    <option value="">Select status...</option>
                    <option v-for="next_status in get_available_statuses(workflow_statuses[editing_status_index]?.id || '')" 
                            :key="next_status.id" :value="next_status.id">
                      {{ next_status.status }}
                    </option>
                  </select>
                </div>
              </div>
            </div>
          </div>

          <!-- Step 2: Transition Type -->
          <div v-if="editing_step === 2" class="fade show">
            <h6 class="fw-bold text-dark mb-3 text-xxs">Edit Transition Type</h6>
            <div class="radio-group">
              <div class="radio-option mb-2" :class="{ 'selected': editing_rule.transition_type_id === 1 }"
                   @click="editing_rule.transition_type_id = 1">
                <input type="radio" name="editTransitionType" value="1" class="radio-input"
                       :checked="editing_rule.transition_type_id === 1"
                       @click="editing_rule.transition_type_id = 1" />
                <div class="radio-content">
                  <div class="radio-title text-xxs fw-bold">Manual Transition</div>
                  <div class="radio-description text-xxs text-muted">Requires explicit approval from reviewers</div>
                </div>
              </div>
              <div class="radio-option" :class="{ 'selected': editing_rule.transition_type_id === 2 }"
                   @click="editing_rule.transition_type_id = 2">
                <input type="radio" name="editTransitionType" value="2" class="radio-input"
                       :checked="editing_rule.transition_type_id === 2"
                       @click="editing_rule.transition_type_id = 2" />
                <div class="radio-content">
                  <div class="radio-title text-xxs fw-bold">Automatic Transition</div>
                  <div class="radio-description text-xxs text-muted">Transitions automatically when conditions are met</div>
                </div>
              </div>
            </div>

            <div v-if="editing_rule.transition_type_id === 1" class="mt-3">
              <h6 class="fw-bold text-dark mb-2 text-xxs">Minimum Approvals Required</h6>
              <input type="number" class="form-control form-control-sm text-xxs" 
                     v-model.number="editing_rule.min_approvals" min="1" />
            </div>
          </div>

          <!-- Step 3: Edit Conditions -->
          <div v-if="editing_step === 3" class="fade show">
            <h6 class="fw-bold text-dark mb-3 text-xxs">
              <i class="bi bi-funnel me-2"></i>Edit Transition Conditions
            </h6>

            <div v-if="editing_rule.conditions.length === 0" class="empty-state border rounded p-4 text-center mb-4">
              <div class="empty-icon mb-2">
                <i class="bi bi-funnel fs-3 text-muted"></i>
              </div>
              <div class="empty-text text-xxs text-muted mb-3">No conditions added yet</div>
              <button type="button" class="btn btn-primary-dark btn-sm rounded-small text-xxs shadow text-dark"
                      @click="add_condition_in_edit">
                Add Condition
              </button>
            </div>

            <div v-for="(condition, condition_index) in editing_rule.conditions" :key="condition_index"
                 class="card border mb-3">
              <div class="card-header bg-white py-2 d-flex justify-content-between align-items-center">
                <span class="fw-semibold text-xxs">Condition {{ condition_index + 1 }}</span>
                <button type="button" class="btn btn-primary-dark btn-sm rounded-small text-xxs shadow text-dark py-1 px-2"
                        @click="remove_condition_in_edit(condition_index)">
                  Remove
                </button>
              </div>

              <div class="card-body">
                <div class="row g-3">
                  <div class="col-md-4">
                    <label class="text-xxs fw-bold mb-1">Action Type</label>
                    <select v-model="condition.action_type" class="form-select form-select-sm text-xxs"
                            @change="on_action_type_change(condition)">
                      <option value="">-- Select Action Type --</option>
                      <option value="section_completion">Section Completion</option>
                      <option value="question_completion">Question Completion</option>
                      <option value="approval">Approval</option>
                    </select>
                  </div>

                  <div class="col-md-4">
                    <label class="text-xxs fw-bold mb-1">Required Role</label>
                    <select v-model="condition.required_role" class="form-select form-select-sm text-xxs">
                      <option value="">-- Select Role --</option>
                      <option v-for="role in roles" :key="role.id" :value="role.id">{{ role.role }}</option>
                    </select>
                  </div>

                  <div class="col-md-4">
                    <label class="text-xxs fw-bold mb-1">Completion Type</label>
                    <select v-model="condition.completion_type" class="form-select form-select-sm text-xxs">
                      <option value="complete">Complete</option>
                      <option value="verified">Verified</option>
                      <option value="approved">Approved</option>
                    </select>
                  </div>
                </div>

                <!-- Section Completion -->
                <div v-if="condition.action_type === 'section_completion'" class="mt-3">
                  <h6 class="fw-bold text-dark mb-1 text-xxs">Select Sections</h6>
                  <div class="border rounded p-2 max-h-150px overflow-auto">
                    <input type="text" class="form-control form-control-sm mb-2 text-xxs" placeholder="Search sections..."
                           v-model="condition.sections_search" />
                    <div v-for="section in section_list.filter(s => !condition.sections_search || (s.name || s.status).toLowerCase().includes(condition.sections_search.toLowerCase()))"
                         :key="section.id" class="form-check mb-1">
                      <input type="checkbox" class="form-check-input"
                             :id="'edit-section-'+condition_index+'-'+section.id"
                             v-model="condition.selected_sections"
                             :value="section.id" />
                      <label class="form-check-label text-xxs" :for="'edit-section-'+condition_index+'-'+section.id">
                        {{ section.name || section.section_name || section.status || `Section ${section.id}` }}
                      </label>
                    </div>
                  </div>
                </div>

                <!-- Question Completion -->
                <div v-if="condition.action_type === 'question_completion'" class="mt-3">
                  <h6 class="fw-bold text-dark mb-1 text-xxs">Select Sections for Questions</h6>
                  <div class="border rounded p-2 max-h-150px overflow-auto">
                    <input type="text" class="form-control form-control-sm mb-2 text-xxs" placeholder="Search sections..."
                           v-model="condition.sections_search" />
                    <div v-for="section in section_list.filter(s => !condition.sections_search || (s.name || s.status).toLowerCase().includes(condition.sections_search.toLowerCase()))"
                         :key="section.id" class="form-check mb-1">
                      <input type="checkbox" class="form-check-input"
                             :id="'edit-q-section-'+condition_index+'-'+section.id"
                             v-model="condition.selected_sections_for_questions"
                             :value="section.id"
                             @change="load_questions_for_selected_sections_edit(condition)" />
                      <label class="form-check-label text-xxs" :for="'edit-q-section-'+condition_index+'-'+section.id">
                        {{ section.name || section.section_name || section.status || `Section ${section.id}` }}
                      </label>
                    </div>
                  </div>

                  <!-- Questions under selected sections -->
                  <div v-for="section_id in condition.selected_sections_for_questions" :key="section_id" class="mt-2">
                    <h6 class="fw-bold text-dark mb-1 text-xxs">Questions in Section {{ get_section_name(section_id) }}</h6>
                    <div class="border rounded p-2 mb-2 max-h-150px overflow-auto">
                      <input type="text" class="form-control form-control-sm mb-2 text-xxs" placeholder="Search questions..."
                            v-model="condition.questions_search_map[section_id]" />
                      
                      <!-- Show loading state -->
                      <div v-if="is_section_loading(section_id)" class="text-center py-2">
                        <div class="spinner-border spinner-border-sm text-primary" role="status">
                          <span class="visually-hidden">Loading...</span>
                        </div>
                        <div class="text-xxs text-muted mt-1">Loading questions...</div>
                      </div>
                      
                      <!-- Show questions when loaded -->
                      <div v-else>
                        <div v-for="question in get_questions_for_section(section_id)
                            .filter(q => {
                              const search = (condition.questions_search_map?.[section_id] || '').toLowerCase().trim();
                              const text = (q.question_text || q.question || q.text || '').toLowerCase();
                              return !search || text.includes(search);
                            })"
                            :key="question.id" class="form-check mb-1">
                          <input type="checkbox" class="form-check-input"
                                :id="'edit-question-'+condition_index+'-'+question.id"
                                v-model="condition.selected_questions"
                                :value="question.id"
                                :checked="condition.selected_questions && condition.selected_questions.includes(parseInt(question.id, 10))" />
                          <label class="form-check-label text-xxs" :for="'edit-question-'+condition_index+'-'+question.id">
                            {{ get_question_display_text(question) }}
                          </label>
                        </div>
                        
                        <!-- Show message if no questions found -->
                        <div v-if="get_questions_for_section(section_id).length === 0" class="text-muted text-xxs py-2">
                          No questions found in this section
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Actions Multi-Select -->
                <div class="mt-3">
                  <h6 class="fw-bold text-dark mb-1 text-xxs">Select Action(s)</h6>
                  <div class="border rounded p-2 max-h-150px overflow-auto">
                    <input type="text" class="form-control form-control-sm mb-2 text-xxs" placeholder="Search actions..."
                          v-model="condition.action_search" />
                    <div v-for="action in get_filtered_actions(condition)" :key="action.id" class="form-check mb-1">
                      <input type="checkbox" class="form-check-input"
                            :id="'edit-action-'+condition_index+'-'+action.id"
                            v-model="condition.selected_actions"
                            :value="action.id" />
                      <label class="form-check-label text-xxs" :for="'edit-action-'+condition_index+'-'+action.id">
                        {{ action.name }} <span class="text-muted">{{ action.description }}</span>
                      </label>
                    </div>
                  </div>
                </div>

                <!-- Approvals Required -->
                <div class="mt-3">
                  <h6 class="fw-bold text-dark mb-1 text-xxs">Approvals Required</h6>
                  <input type="number" class="form-control form-control-sm text-xxs" 
                         v-model.number="condition.approvals_required" min="1" />
                </div>
              </div>
            </div>

            <div v-if="editing_rule.conditions.length > 0" class="mt-3">
              <button type="button" class="btn btn-primary-dark btn-sm rounded-small text-xxs shadow text-dark"
                      @click="add_condition_in_edit">
                Add Another Condition
              </button>
            </div>
          </div>

          <!-- Step 4: Review & Update -->
          <div v-if="editing_step === 4" class="fade show">
            <h6 class="fw-bold text-dark mb-3 text-xxs">Review Updated Transition Rule</h6>
            <div class="summary-card border rounded p-3 mb-3">
              <h6 class="fw-bold text-dark text-xxs mb-2">Transition Rule Summary</h6>
              <div class="summary-row d-flex justify-content-between mb-2">
                <span class="summary-label text-xxs text-muted">Status Flow:</span>
                <span class="summary-value text-xxs fw-bold">
                  {{ workflow_statuses[editing_status_index]?.status || '' }} → {{ get_status_name(editing_rule.to_status_id) }}
                </span>
              </div>
              <div class="summary-row d-flex justify-content-between mb-2">
                <span class="summary-label text-xxs text-muted">Transition Type:</span>
                <span class="summary-value text-xxs fw-bold">
                  {{ editing_rule.transition_type_id === 1 ? 'Manual' : 'Automatic' }}
                </span>
              </div>
              <div v-if="editing_rule.transition_type_id === 1" class="summary-row d-flex justify-content-between mb-2">
                <span class="summary-label text-xxs text-muted">Min Approvals:</span>
                <span class="summary-value text-xxs fw-bold">{{ editing_rule.min_approvals }}</span>
              </div>
              <div class="summary-row d-flex justify-content-between mb-2">
                <span class="summary-label text-xxs text-muted">Match Requirement:</span>
                <span class="summary-value text-xxs fw-bold">
                  {{ editing_rule.match_requirement === 'all' ? 'All conditions' : 'Any condition' }}
                </span>
              </div>
              <div class="summary-row d-flex justify-content-between">
                <span class="summary-label text-xxs text-muted">Conditions:</span>
                <span class="summary-value text-xxs fw-bold">{{ editing_rule.conditions.length }} condition(s)</span>
              </div>
            </div>

            <!-- Conditions Details -->
            <div v-if="editing_rule.conditions.length > 0" class="conditions-summary border rounded p-3">
              <h6 class="fw-bold text-dark text-xxs mb-3">Conditions Details</h6>
              <div v-for="(condition, index) in editing_rule.conditions" :key="index" class="mb-2">
                <div class="text-xxs">
                  <span class="fw-semibold">Condition {{ index + 1 }}:</span>
                  <span class="text-muted ms-2">
                    {{ condition.action_type === 'section_completion' ? 'Section Completion' : 
                       condition.action_type === 'question_completion' ? 'Question Completion' : 
                       condition.action_type === 'approval' ? 'Approval' : 'Unknown' }}
                    - {{ condition.selected_action?.length || 0 }} action(s)
                    {{ condition.required_role ? `- Role: ${get_role_name(condition.required_role)}` : '' }}
                  </span>
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>

      <!-- Footer -->
      <div class="modal-footer border-0">
        <button type="button" class="btn btn-primary-dark btn-sm rounded-small text-xxs shadow text-dark" 
                v-if="editing_step > 1" @click="go_to_previous_edit_step">
          Back
        </button>
        <button type="button" class="btn btn-primary-dark btn-sm rounded-small text-xxs shadow text-dark"
                @click="go_to_next_edit_step">
          {{ is_edit_ready_to_update ? 'Update Transition Rule' : 'Next' }}
        </button>
      </div>

    </div>
  </div>
</div>

  <!-- Section Access Modal -->
<teleport to="body">
  <div class="modal fade" id="section_access_modal" tabindex="-1" aria-labelledby="section_access_modal_label"
      aria-hidden="true">
      <div class="modal-dialog modal-lg">
          <div class="modal-content bg-white rounded-3">
              <div class="modal-header border-bottom-0 custom-header text-white">
                  <h5 class="modal-title text-xxs" id="section_access_modal_label">
                      Manage Section Access 
                  </h5>
                  <button type="button" class="modal-btn-close btn-close" data-bs-dismiss="modal" aria-label="Close"
                      @click="reset_section_access_modal"></button>
              </div>

          <div class="modal-body py-5">
            <!-- Info Note -->
            <div class="alert alert-info alert-dismissible fade show mb-3" role="alert">
              <div class="d-flex align-items-center">
                <i class="fas fa-info-circle me-2"></i>
                <div class="text-xxs">
                  <strong>Note:</strong>
                  <span class="text-info">Read Only</span> allows you to view sections.
                  <span class="text-success">Write Access</span> allows you to read and edit sections.
                  You can only have one type of access assigned at a time.
                </div>
              </div>
            </div>

            <table id="assign_form_status_table" class="table-responsive w-100 table table-hover">
              <thead class="custom-header">
                <tr>
                  <th class="text-uppercase text-xxs font-weight-bolder opacity-7 text-start">Section Name</th>
                  <th class="text-uppercase text-xxs font-weight-bolder opacity-7 text-start">Read Access</th>
                  <th class="text-uppercase text-xxs font-weight-bolder opacity-7 text-start">Write Access</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="section in sections_with_access" :key="section.id">
                  <td class="text-xxs text-start">{{ section.name }}</td>

                  <td class="text-xxs text-start w-25">
                    <div class="form-check form-switch d-flex">
                      <input class="form-check-input" type="checkbox" :id="'read_' + section.id"
                        :checked="section.has_read" :disabled="section.has_write || !read_action_id || isLoading"
                        @change="toggle_read_access(section)">
                    </div>
                  </td>

                  <td class="text-xxs text-start w-25">
                    <div class="form-check form-switch d-flex">
                      <input class="form-check-input" type="checkbox" :id="'write_' + section.id"
                        :checked="section.has_write" :disabled="section.has_read || !write_action_id || isLoading"
                        @change="toggle_write_access(section)">
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>

            <p v-if="sections_with_access.length === 0" class="text-center text-xxs text-muted py-4">
              No sections found for this form.
            </p>
          </div>
        </div>
      </div>
    </div>
</teleport>
</template>